import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Eye, Shield } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingStories, setPendingStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchPendingStories();
  }, [user, navigate]);

  const fetchPendingStories = async () => {
    try {
      const response = await axios.get(`${API}/stories/pending`);
      setPendingStories(response.data);
    } catch (error) {
      console.error('Failed to fetch pending stories:', error);
      toast.error('Kon verhalen niet ophalen');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (storyId) => {
    try {
      await axios.post(`${API}/stories/${storyId}/approve`);
      setPendingStories(stories => stories.filter(s => s.id !== storyId));
      toast.success('Verhaal goedgekeurd! 📚');
    } catch (error) {
      toast.error('Goedkeuren mislukt');
    }
  };

  const handleReject = async (storyId) => {
    try {
      await axios.delete(`${API}/stories/${storyId}`);
      setPendingStories(stories => stories.filter(s => s.id !== storyId));
      toast.success('Verhaal verwijderd');
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="admin-loading">
        <div className="max-w-4xl mx-auto space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="admin-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-display text-4xl text-foreground mb-2">
              Beheerder Dashboard
            </h1>
            <p className="text-muted-foreground font-accent">
              Keur nieuwe verhalen goed of wijs ze af
            </p>
          </div>

          {pendingStories.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <div className="text-6xl mb-4">✨</div>
                <h3 className="font-display text-2xl text-foreground mb-2">
                  Geen wachtende verhalen
                </h3>
                <p className="text-muted-foreground font-accent">
                  Alle verhalen zijn beoordeeld!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingStories.map((story, index) => (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-2 border-border overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      {/* Cover image */}
                      {story.cover_image_url && (
                        <div 
                          className="w-full md:w-48 h-40 md:h-auto bg-cover bg-center"
                          style={{ backgroundImage: `url(${story.cover_image_url})` }}
                        />
                      )}
                      
                      <div className="flex-1">
                        <CardHeader>
                          <CardTitle className="font-display text-xl flex items-center justify-between">
                            <span>{story.title}</span>
                            <span className="text-sm font-accent text-muted-foreground">
                              door {story.grandma_name}
                            </span>
                          </CardTitle>
                        </CardHeader>
                        
                        <CardContent>
                          <p className="text-muted-foreground mb-4 line-clamp-2">
                            {story.description}
                          </p>
                          
                          <p className="text-sm text-muted-foreground mb-4 font-accent">
                            {story.pages?.length || 0} pagina's
                          </p>

                          <div className="flex gap-2">
                            <Button
                              data-testid={`preview-story-${story.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/story/${story.id}`)}
                              className="rounded-full"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Bekijken
                            </Button>
                            
                            <Button
                              data-testid={`approve-story-${story.id}`}
                              size="sm"
                              onClick={() => handleApprove(story.id)}
                              className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Goedkeuren
                            </Button>
                            
                            <Button
                              data-testid={`reject-story-${story.id}`}
                              variant="destructive"
                              size="sm"
                              onClick={() => handleReject(story.id)}
                              className="rounded-full"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Afwijzen
                            </Button>
                          </div>
                        </CardContent>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Admin;
